import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import PillButton from './PillButton'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { Spinner } from '@astryxdesign/core/Spinner'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { useToast } from '@astryxdesign/core/Toast'
import { buildManuscriptDiffRows, type ManuscriptDiffRow } from '../lib/manuscriptDiff'
import type { DiffLine } from '../utils/lineDiff'

interface ChapterHistoryDialogProps {
  isOpen: boolean
  storyId: string
  chapterId: string
  currentContent: string
  onClose: () => void
  onRestored: (content: string) => void
}

const sameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()

const dayLabel = (timestamp: number): string => {
  const date = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (sameDay(date, today)) return 'Today'
  if (sameDay(date, yesterday)) return 'Yesterday'
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

const timeLabel = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

export default function ChapterHistoryDialog({
  isOpen,
  storyId,
  chapterId,
  currentContent,
  onClose,
  onRestored
}: ChapterHistoryDialogProps): React.JSX.Element {
  const showToast = useToast()
  const [revisions, setRevisions] = useState<ChapterRevisionSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ChapterRevisionDetail | null>(null)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailReloadToken, setDetailReloadToken] = useState(0)
  const [isConfirmingRestore, setIsConfirmingRestore] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [viewMode, setViewMode] = useState<'full' | 'changes'>('full')

  const loadRevisions = useCallback(async (): Promise<void> => {
    setIsLoadingList(true)
    setListError(null)
    try {
      const loaded = await window.api.chapterRevisions.list(storyId, chapterId)
      setRevisions(loaded)
      setSelectedId(loaded[0]?.id ?? null)
    } catch (err) {
      console.error('Failed to load chapter history:', err)
      setListError('Could not load version history.')
    } finally {
      setIsLoadingList(false)
    }
  }, [chapterId, storyId])

  // These three effects synchronize dialog state with props (isOpen, selectedId) rather than
  // responding to external events, so the setState calls in their bodies are intentional —
  // matching the same idiom already accepted (and disabled) in StoryCanvas.tsx.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isOpen) return
    void loadRevisions()
  }, [isOpen, loadRevisions])

  // Clear the previous chapter's preview so a stale body never flashes on reopen.
  useEffect(() => {
    if (isOpen) return
    setRevisions([])
    setSelectedId(null)
    setDetail(null)
    setListError(null)
    setDetailError(null)
    setIsConfirmingRestore(false)
    setViewMode('full')
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !selectedId) {
      setDetail(null)
      return
    }
    let isCurrent = true
    setIsConfirmingRestore(false)
    setIsLoadingDetail(true)
    setDetailError(null)
    window.api.chapterRevisions
      .get(storyId, chapterId, selectedId)
      .then((loaded) => {
        if (isCurrent) setDetail(loaded)
      })
      .catch((err) => {
        console.error('Failed to load revision:', err)
        if (isCurrent) setDetailError('Could not load this version.')
      })
      .finally(() => {
        if (isCurrent) setIsLoadingDetail(false)
      })
    return () => {
      isCurrent = false
    }
  }, [chapterId, detailReloadToken, isOpen, selectedId, storyId])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleRestore = async (): Promise<void> => {
    if (!selectedId) return
    setIsRestoring(true)
    try {
      const { content } = await window.api.chapterRevisions.restore(storyId, chapterId, selectedId)
      onRestored(content)
    } catch (err) {
      console.error('Failed to restore revision:', err)
      showToast({
        body: 'Could not restore this version. Nothing was changed.',
        type: 'error'
      })
    } finally {
      setIsRestoring(false)
      setIsConfirmingRestore(false)
    }
  }

  // Base is the selected version, target is the live draft — so removals are text that this
  // version had and the draft no longer does, and additions are text written since.
  const diffRows = useMemo(
    () => (detail ? buildManuscriptDiffRows(detail.content, currentContent) : []),
    [currentContent, detail]
  )
  const hasChanges = diffRows.some((row) => row.change !== 'same')

  const renderWordRuns = (
    words: DiffLine[],
    highlightType: 'added' | 'removed'
  ): React.ReactNode => {
    const runs: DiffLine[] = []
    for (const word of words) {
      const last = runs[runs.length - 1]
      if (last && last.type === word.type) {
        last.text += word.text
      } else {
        runs.push({ type: word.type, text: word.text })
      }
    }
    return runs.map((run, index) => (
      <span
        key={index}
        className={
          run.type === highlightType ? `chapter-history-diff-word-${highlightType}` : undefined
        }
      >
        {run.text}
      </span>
    ))
  }

  // Markers are real content, not aria-hidden decoration: colour alone would be the only
  // signal of what changed for anyone who can't distinguish the row tints.
  const diffRow = (key: number, row: ManuscriptDiffRow): React.ReactNode => (
    <p key={key} className={`chapter-history-diff-row is-${row.change} block-${row.kind}`}>
      <span className="chapter-history-diff-marker">
        {row.change === 'added' ? '+' : row.change === 'removed' ? '\u2212' : ''}
      </span>
      <span className="chapter-history-diff-text">
        {row.kind === 'bullet' && <span className="chapter-history-diff-bullet">{'\u2022'}</span>}
        {row.kind === 'number' && (
          <span className="chapter-history-diff-bullet">{row.ordinal}.</span>
        )}
        {row.words
          ? renderWordRuns(row.words, row.change === 'added' ? 'added' : 'removed')
          : row.text}
      </span>
    </p>
  )

  let previousDay = ''

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isRestoring) onClose()
      }}
      width={1200}
      maxHeight="calc(100vh - 64px)"
      padding={0}
      purpose="form"
      className="chapter-history-dialog"
    >
      <section className="chapter-history-shell">
        <DialogHeader
          title="Chapter History"
          className="chapter-history-header"
          onOpenChange={() => {
            if (!isRestoring) onClose()
          }}
        />
        <section className="chapter-history-body">
          <nav className="chapter-history-list" aria-label="Saved versions">
            {isLoadingList && (
              <VStack gap={2} align="center" className="chapter-history-state">
                <Spinner size="sm" label="Loading versions" />
              </VStack>
            )}

            {!isLoadingList && listError && (
              <VStack gap={2} className="chapter-history-state">
                <Text type="supporting">{listError}</Text>
                <PillButton
                  variant="secondary"
                  size="sm"
                  label="Retry"
                  onClick={() => void loadRevisions()}
                />
              </VStack>
            )}

            {!isLoadingList && !listError && revisions.length === 0 && (
              <VStack gap={2} className="chapter-history-state">
                <Text type="supporting">
                  No earlier versions yet. Versions are saved automatically as you write.
                </Text>
              </VStack>
            )}

            {!isLoadingList &&
              !listError &&
              revisions.map((revision) => {
                const label = dayLabel(revision.createdAt)
                const showHeading = label !== previousDay
                previousDay = label
                return (
                  <React.Fragment key={revision.id}>
                    {showHeading && (
                      <Text type="label" className="chapter-history-day">
                        {label}
                      </Text>
                    )}
                    <button
                      type="button"
                      className={`chapter-history-item${
                        selectedId === revision.id ? ' is-selected' : ''
                      }`}
                      aria-current={selectedId === revision.id ? 'true' : undefined}
                      onClick={() => setSelectedId(revision.id)}
                      disabled={isRestoring}
                    >
                      <HStack justify="between" gap={3}>
                        <Text type="label">{timeLabel(revision.createdAt)}</Text>
                        <Text type="supporting">
                          {revision.wordCount.toLocaleString()}{' '}
                          {revision.wordCount === 1 ? 'word' : 'words'}
                        </Text>
                      </HStack>
                      <span className="chapter-history-excerpt">{revision.excerpt}</span>
                    </button>
                  </React.Fragment>
                )
              })}
          </nav>

          <section className="chapter-history-preview" aria-label="Version preview">
            {isLoadingDetail && <Spinner size="sm" label="Loading version" />}
            {!isLoadingDetail && detailError && (
              <VStack gap={2} className="chapter-history-state">
                <Text type="supporting">{detailError}</Text>
                <PillButton
                  variant="secondary"
                  size="sm"
                  label="Retry"
                  onClick={() => setDetailReloadToken((value) => value + 1)}
                />
              </VStack>
            )}
            {!isLoadingDetail && !detailError && detail && (
              <>
                <HStack
                  justify="between"
                  align="center"
                  gap={3}
                  className="chapter-history-preview-bar"
                >
                  <SegmentedControl
                    value={viewMode}
                    onChange={(value) => setViewMode(value === 'changes' ? 'changes' : 'full')}
                    label="Version view"
                    size="sm"
                  >
                    <SegmentedControlItem value="full" label="Full" />
                    <SegmentedControlItem
                      value="changes"
                      label="Changes"
                      isDisabled={!hasChanges}
                    />
                  </SegmentedControl>
                  {viewMode === 'changes' && hasChanges && (
                    <Text type="supporting">
                      Comparing this version with your current draft. Restoring undoes these
                      changes.
                    </Text>
                  )}
                </HStack>

                {viewMode === 'full' && (
                  <article
                    className="manuscript-prose chapter-history-preview-body"
                    // The writer's own manuscript markup, the same content the editor renders.
                    dangerouslySetInnerHTML={{ __html: detail.content }}
                  />
                )}

                {viewMode === 'changes' && !hasChanges && (
                  <Text type="supporting">This version matches your current draft.</Text>
                )}

                {viewMode === 'changes' && hasChanges && (
                  <div className="chapter-history-diff">
                    {diffRows.map((row, index) => diffRow(index, row))}
                  </div>
                )}
              </>
            )}
          </section>
        </section>

        <footer className="chapter-history-footer">
          {/* Deliberately no number: the cap lives in MAX_REVISIONS_PER_CHAPTER on the server,
              which this bundle cannot import across the tsconfig split, so any figure here
              would silently start lying the moment that constant changed. */}
          <Text type="supporting">Showing the most recent versions of this chapter.</Text>
          {isConfirmingRestore ? (
            <HStack gap={2} align="center">
              <Text type="supporting">This replaces the current chapter.</Text>
              <PillButton
                variant="secondary"
                label="Cancel"
                onClick={() => setIsConfirmingRestore(false)}
                isDisabled={isRestoring}
              />
              <PillButton
                variant="primary"
                label="Restore"
                onClick={() => void handleRestore()}
                isLoading={isRestoring}
              />
            </HStack>
          ) : (
            <PillButton
              variant="secondary"
              label="Restore this version"
              onClick={() => setIsConfirmingRestore(true)}
              // Also disabled when this version already matches the draft: restoring then
              // changes nothing, but still writes an unconditional pre-restore snapshot, so
              // repeated exploratory restores would burn real history slots on duplicates.
              isDisabled={!detail || isLoadingDetail || !hasChanges}
            />
          )}
        </footer>
      </section>
    </Dialog>
  )
}
